// 2026-09-29：管理端按账号保存提示音，增量收件箱去重，温和音色区分普通与紧急节奏。
export type SoundLevel = 'normal' | 'urgent'
// 2026-09-30：试听返回明确结果，区分静音、初始化、恢复失败与实际已调度音频。
export type SoundPreviewResult = 'played' | 'disabled' | 'muted' | 'unsupported' | 'inactive' | 'blocked' | 'failed' | 'cancelled'
export interface SoundState { enabled: boolean; volume: number; ready: boolean; supported: boolean }
// 服务端 NON_EMPTY 序列化会省略空消息数组，首次基线和空轮询仍须接收游标。
export interface SoundPage { cursor: number; messages?: Array<{ id: number; soundLevel: SoundLevel }>; hasMore: boolean }
export interface SoundPlayer {
  unlock(): Promise<boolean>
  play(level: SoundLevel, volume: number): boolean
  stop(): void
  close(): void
  available(): boolean
}
export interface SoundDependencies {
  state: SoundState
  storage: Pick<Storage, 'getItem' | 'setItem'>
  player: SoundPlayer
  fetch: (afterId?: number) => Promise<SoundPage>
  schedule: (callback: () => void, delay: number) => number
  cancel: (id: number) => void
  deliver?: (identity: string, cursor: number, emit: (afterId: number) => boolean) => Promise<void>
}
export const soundStorageKey = (identity: string): string => 'landiyun:notification-sound:v1:' + identity
const safeVolume = (value: unknown): number => typeof value === 'number' && Number.isFinite(value)
  ? Math.max(0, Math.min(100, Math.round(value))) : 35

export function createNotificationSound(deps: SoundDependencies) {
  const { state, player } = deps
  let identity = '', generation = 0, timer = 0, cursor: number | undefined
  let previewRevision = 0
  const save = () => {
    if (!identity) return
    try { deps.storage.setItem(soundStorageKey(identity), JSON.stringify({ enabled: state.enabled, volume: state.volume })) } catch (_) { /* 本次会话仍有效。 */ }
  }
  const read = () => {
    let value: any
    try { value = JSON.parse(deps.storage.getItem(soundStorageKey(identity)) || 'null') } catch (_) { value = null }
    state.enabled = value?.enabled !== false
    state.volume = safeVolume(value?.volume)
    if (!state.enabled || !state.volume) player.stop()
  }
  const poll = async (revision: number) => {
    if (!identity || revision !== generation) return
    try {
      const fresh: NonNullable<SoundPage['messages']> = []
      // 有界合并最多五页，避免大量普通消息把后面的紧急提醒每页延迟十秒。
      for (let count = 0; count < 5; count++) {
        let page: SoundPage
        try { page = await deps.fetch(cursor) } catch (_) { break }
        if (revision !== generation) return
        if (!page || !Number.isSafeInteger(page.cursor) || page.cursor < (cursor ?? 0)) return
        const messages = page.messages === undefined ? [] : page.messages
        if (!Array.isArray(messages)) return
        const previous = cursor
        if (previous !== undefined) fresh.push(...messages.filter(item =>
          Number.isSafeInteger(item.id) && item.id > previous && item.id <= page.cursor))
        cursor = page.cursor
        if (!page.hasMore || previous === undefined || cursor === previous) break
      }
      // 每轮只响一次，紧急优先；关闭或浏览器尚未允许声音时也前移游标，不积压补响。
      const emit = (afterId: number) => {
        const remaining = fresh.filter(item => item.id > afterId)
        if (revision !== generation || !remaining.length || !state.enabled || !state.ready || state.volume <= 0) return false
        // 2026-09-30：未实际播放时不领取跨标签水位，并等待下一次用户操作恢复音频。
        try {
          if (player.play(remaining.some(item => item.soundLevel === 'urgent') ? 'urgent' : 'normal', state.volume) !== false) return true
        } catch (_) { /* 播放失败不能影响收件或管理操作。 */ }
        state.ready = false
        return false
      }
      if (fresh.length && state.enabled && state.ready && state.volume > 0) {
        if (deps.deliver) await deps.deliver(identity, cursor!, emit)
        else emit(0)
      }
    } catch (_) { /* 查单失败保留游标，下轮重试；声音不能影响管理操作。 */ }
    finally {
      if (identity && revision === generation) timer = deps.schedule(() => { void poll(revision) }, 10000)
    }
  }
  const rebase = () => {
    generation++; previewRevision++; deps.cancel(timer); timer = 0; cursor = undefined
    if (identity) void poll(generation)
  }
  const stop = () => {
    generation++; previewRevision++; deps.cancel(timer); timer = 0; identity = ''; cursor = undefined
    state.ready = false; player.stop()
  }
  const start = (nextIdentity: string) => {
    if (identity && identity === nextIdentity) return
    stop()
    identity = nextIdentity
    state.supported = player.available()
    if (!identity) return
    read(); void poll(generation)
  }
  const unlock = async (): Promise<boolean> => {
    if (!identity || !state.enabled || !state.supported) return false
    const revision = generation
    try {
      const ready = await player.unlock()
      if (revision === generation) state.ready = ready
      return revision === generation && ready
    } catch (_) { if (revision === generation) state.ready = false; return false }
  }
  return {
    start, stop, unlock,
    setEnabled(enabled: boolean) {
      const wasEnabled = state.enabled
      state.enabled = enabled; save()
      if (!enabled) { previewRevision++; player.stop() } else {
        // 重新开启先建立当前基线，覆盖静音期间断网或尚未拉取的消息，不补响。
        if (!wasEnabled) rebase()
        void unlock()
      }
    },
    setVolume(volume: number) { state.volume = safeVolume(volume); save(); if (!state.volume) player.stop() },
    // 2026-09-30：保留用户静音和音量设置，失败返回原因，关闭/切账号/后续试听会取消旧请求。
    async preview(level: SoundLevel): Promise<SoundPreviewResult> {
      const revision = ++previewRevision
      if (!state.supported) return 'unsupported'
      if (!identity) return 'inactive'
      if (!state.enabled) return 'disabled'
      if (state.volume <= 0) return 'muted'
      const ready = await unlock()
      if (revision !== previewRevision) return 'cancelled'
      if (!state.enabled) return 'disabled'
      if (state.volume <= 0) return 'muted'
      if (!ready) return 'blocked'
      try {
        if (player.play(level, state.volume) !== false) return 'played'
      } catch (_) { /* 页面会显示播放失败，不伪报已开启即有声音。 */ }
      state.ready = false
      return 'failed'
    },
    storageChanged(key: string | null) {
      if (identity && (key === null || key === soundStorageKey(identity))) {
        const wasEnabled = state.enabled
        read()
        if (!wasEnabled && state.enabled) rebase()
      }
    },
    dispose() { stop(); player.close() }
  }
}

// 2026-09-29：同浏览器多标签串行领取声音水位，避免同一通知叠加响；仅存ID和短时水位。
export function createSoundDelivery(storage: Pick<Storage, 'getItem' | 'setItem'>,
  withLock: (name: string, action: () => void) => Promise<void>, now: () => number = Date.now) {
  return async (identity: string, cursor: number, emit: (afterId: number) => boolean): Promise<void> => {
    const key = soundStorageKey(identity) + ':last-played'
    await withLock(key, () => {
      let afterId = 0
      try {
        const previous = JSON.parse(storage.getItem(key) || 'null')
        if (previous && Number.isSafeInteger(previous.id) && typeof previous.at === 'number'
          && now() >= previous.at && now() - previous.at < 120000) afterId = previous.id
      } catch (_) { /* 限制存储的浏览器仍可在本页提示。 */ }
      if (cursor <= afterId || !emit(afterId)) return
      try { storage.setItem(key, JSON.stringify({ id: cursor, at: now() })) } catch (_) { /* 不影响声音与业务。 */ }
    })
  }
}

// 原创轻柔双正弦音色，同一音量下以节奏区分紧急；渐入渐出避免爆音，不使用警笛或循环闹铃。
export const SOUND_NOTES: Record<SoundLevel, Array<[number, number, number]>> = {
  normal: [[0, 587.33, 0.22], [0.26, 783.99, 0.28], [0.57, 659.25, 0.36]],
  urgent: [[0, 587.33, 0.12], [0.15, 783.99, 0.12], [0.30, 659.25, 0.15], [0.53, 587.33, 0.12], [0.68, 783.99, 0.12], [0.83, 880, 0.20]]
}
export function createChimePlayer(getContext: () => AudioContext | null, onReadyChange?: (ready: boolean) => void): SoundPlayer {
  let context: AudioContext | null = null
  const active = new Set<OscillatorNode>()
  const stop = () => {
    active.forEach(node => { try { node.stop() } catch (_) { /* 已停止。 */ } }); active.clear()
  }
  return {
    available: () => true,
    async unlock() {
      if (!context || context.state === 'closed') {
        context = getContext()
        if (context) {
          const current = context
          current.onstatechange = () => { if (context === current && onReadyChange) onReadyChange(current.state === 'running') }
        }
      }
      if (!context) return false
      const current = context
      // 2026-09-30：恢复所有非运行状态，兼容中断后的音频；恢复无回包时两秒结束，不迟到补播。
      if (current.state !== 'running') {
        let timer: ReturnType<typeof setTimeout> | undefined
        try {
          const resumed = await Promise.race([
            current.resume().then(() => true),
            new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), 2000) })
          ])
          if (!resumed) return false
        } finally { if (timer !== undefined) clearTimeout(timer) }
      }
      return context === current && current.state === 'running'
    },
    play(level, volume) {
      if (!context || context.state !== 'running' || volume <= 0) return false
      stop()
      const start = context.currentTime + 0.02
      for (const [offset, frequency, duration] of SOUND_NOTES[level]) {
        const gain = context.createGain(), oscillator = context.createOscillator()
        const at = start + offset, amplitude = 0.12 * safeVolume(volume) / 100
        oscillator.type = 'sine'; oscillator.frequency.value = frequency
        gain.gain.setValueAtTime(0, at)
        gain.gain.linearRampToValueAtTime(amplitude, at + 0.025)
        gain.gain.exponentialRampToValueAtTime(0.0001, at + duration)
        gain.gain.linearRampToValueAtTime(0, at + duration + 0.02)
        oscillator.connect(gain); gain.connect(context.destination); active.add(oscillator)
        oscillator.onended = () => { active.delete(oscillator); oscillator.disconnect(); gain.disconnect() }
        oscillator.start(at); oscillator.stop(at + duration + 0.03)
      }
      return true
    },
    stop,
    close() { stop(); if (context) { void context.close().catch(() => undefined); context = null } }
  }
}
